import {
  createQuery,
  createSuspenseQuery
} from "../../utils/queryFactories.js";
import { api } from "../../utils/fetch.js";
import { z } from "zod";

const GetContactsResponseSchema = z.object({
  id: z.number(),
  board: z.number(),
  name: z.string().nullish(),
  phone: z.string().nullish(),
  email: z.string().nullish(),
  address: z.string().nullish(),
  notes_text: z.string().nullish(),
  category: z.string().nullish(),
  status: z.string().nullish(),
  tag: z.array(z.string().nullish())
});

const wfRequest = z.object({
  api: z.literal("wf"),
  id: z.string()
});

const publicRequest = z.object({
  api: z.literal("public"),
  id: z.string(),
  slug: z.string()
});

const ContactsRequestSchema = z.discriminatedUnion("api", [
  wfRequest,
  publicRequest
]);

const ContactsRequestVariables = z.union([
  wfRequest.omit({ api: true }),
  publicRequest.omit({ api: true })
]);

export type ContactsRequest = z.infer<typeof ContactsRequestSchema>;
export type ContactsResponse = z.infer<typeof GetContactsResponseSchema>;

const urls = {
  wf: "/contact/:id",
  public: "/public/:slug/contact/:id"
};

export const useContact = createQuery<ContactsResponse, ContactsRequest, Error>(
  {
    queryKey: ["contact"],
    staleTime: 1000 * 60,
    fetcher: async (variables) => {
      const { api: contactAPI, ...rest } = variables;
      return api<Omit<ContactsRequest, "api">, ContactsResponse>({
        method: "GET",
        url: urls[contactAPI],
        requestSchema: ContactsRequestVariables,
        responseSchema: GetContactsResponseSchema,
        body: rest
      });
    }
  }
);

export const useContactWithSuspense = createSuspenseQuery<
  ContactsResponse,
  ContactsRequest,
  Error
>({
  queryKey: ["contact"],
  staleTime: 1000 * 60,
  fetcher: async (variables) => {
    const { api: contactAPI, ...rest } = variables;
    return api<Omit<ContactsRequest, "api">, ContactsResponse>({
      method: "GET",
      url: urls[contactAPI],
      requestSchema: ContactsRequestVariables,
      responseSchema: GetContactsResponseSchema,
      body: rest
    });
  }
});
